import { CardCategory, Col, Row } from "@/components";
import { ArrowLeft, File, PlayVideo } from "@/icons";
import { Pause } from "@/icons/Pause";
import { Tags } from "@/index";
import { splitInTwo } from "@/utils/splitInTwo";
import { GetValues } from "@anoki/fse-common/dist";
import clsx from "clsx";
import {
  CSSProperties,
  ElementType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { A11y, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "./Slider.scss";

export type Slide = {
  category?: {
    type: string;
    published_at: string;
  };
  src: string;
  alt: string;
  slideTitle?: string;
  title: string;
  description?: string;
  titleAlignment?: "left" | "center" | "right";
  blurPercentage?: number;
  href?: string;
  tags?: string[];
};
export enum SliderTypeEnum {
  FULL_IMAGE = "full-image",
  BLUR_IMAGE = "blur-image",
}

export type SliderType = GetValues<SliderTypeEnum>;
export type SliderProps = {
  slides: Slide[];
  asImage: React.ComponentType<any> | "img";
  asLink?: ElementType;
  timeout?: number;
  openInNewTab?: boolean;
  paginationLabel?: string;
  withShadow?: boolean;
  gradientColor?: string;
  type?: SliderType;
  swiperHeight?: number;
  tagsLabelTitle?: string;
};

type BlurImageSlideProps = {
  tagsLabelTitle?: SliderProps["tagsLabelTitle"];
  ImageComponent: React.ComponentType<any> | "img";
  gradientStyle?: CSSProperties;
  withShadow?: boolean;
} & Slide;

export const BlurImageSlide = ({
  ImageComponent,
  tagsLabelTitle = "Argomenti correlati",
  gradientStyle,
  withShadow,
  ...slide
}: BlurImageSlideProps) => {
  const blurPercentageStyle = {
    "--blur-percentage": slide?.blurPercentage
      ? `${slide?.blurPercentage}px`
      : "5px",
  } as CSSProperties;

  const [firstTitleLine, secondTitleLine] = useMemo(() => {
    if (!slide.slideTitle || slide.slideTitle.trim() === "") {
      return [null, null];
    }

    const words = slide.slideTitle.trim().split(/\s+/);

    if (words.length >= 4) {
      return splitInTwo(slide.slideTitle.toUpperCase());
    }

    return [slide.slideTitle.toUpperCase(), null];
  }, [slide.slideTitle]);

  return (
    <div
      className={clsx("is-event", "hero-event-container", {
        "with-shadow": withShadow,
      })}
      style={gradientStyle}
    >
      <img
        className="hero-event-background"
        src={slide.src}
        alt={`Evento ${slide.alt}`}
        style={blurPercentageStyle}
      />
      <div className={"hero-event-content"}>
        <div className="image-container">
          {!!slide.src && (
            <ImageComponent
              alt={`${slide.title} image`}
              src={slide.src}
              style={{ aspectRatio: 21 / 9 }}
            />
          )}
          {slide.slideTitle && (
            <p className={clsx("short-title", slide.titleAlignment)}>
              <span>{firstTitleLine}</span>
              {secondTitleLine && <span>{secondTitleLine}</span>}
            </p>
          )}
        </div>

        <Col className="hero-event-card-container">
          <div>
            {
              <CardCategory
                title={slide.title}
                href={slide.href || "#"}
                className={"w-full"}
                description2={slide.description}
                classNameTitle={"text-slate-20 fs-5 md-fs-custom-2"}
                classNameDescriptionTitle={
                  "text-slate-28 fw-bold fs-6 md-fs-custom"
                }
              />
            }
          </div>

          {slide.tags && slide.tags?.length > 0 && (
            <Tags title={tagsLabelTitle} items={slide.tags ?? []} />
          )}
        </Col>
      </div>
    </div>
  );
};

export type AutoplayButtonProps = {
  autoplay: boolean;
  toggleAutoPlay: () => void;
};

export const AutoplayButton = ({
  autoplay,
  toggleAutoPlay,
}: AutoplayButtonProps) => {
  return (
    <button
      type="button"
      onClick={toggleAutoPlay}
      aria-label={autoplay ? "Metti in pausa" : "Avvia riproduzione automatica"}
    >
      {autoplay ? (
        <Pause width={20} height={20} />
      ) : (
        <PlayVideo width={20} height={20} color="white" />
      )}
    </button>
  );
};

export type SliderPaginationProps = {
  slides: Slide[];
  currentIndex: number;
  goPrev: () => void;
  goNext: () => void;
  autoplay: boolean;
  toggleAutoPlay: () => void;
  gradientColorStyle: CSSProperties;
  paginationLabel?: string;
  type: SliderType;
};

export const SliderPagination = ({
  slides,
  currentIndex,
  goPrev,
  goNext,
  autoplay,
  toggleAutoPlay,
  gradientColorStyle,
  type,
  paginationLabel = "di",
}: SliderPaginationProps) => {
  return (
    <Row
      className={clsx("pagination", type)}
      gap="x20"
      style={gradientColorStyle}
    >
      {slides.length > 1 && (
        <Row alignItems="center">
          <button
            type="button"
            onClick={goPrev}
            aria-label="Slide precedente"
          >
            <ArrowLeft width={40} height={40} color="white" />
          </button>
          <AutoplayButton autoplay={autoplay} toggleAutoPlay={toggleAutoPlay} />
        </Row>
      )}
      <span>{`${currentIndex + 1} ${paginationLabel} ${slides.length}`}</span>
      {slides.length > 1 && (
        <button
          type="button"
          onClick={goNext}
          aria-label="Slide successiva"
          className="next"
        >
          <ArrowLeft width={40} height={40} color="white" />
        </button>
      )}
    </Row>
  );
};

export const Slider: React.FC<SliderProps> = ({
  slides = [],
  asLink = "a",
  asImage = "img",
  timeout = 3000,
  openInNewTab = false,
  paginationLabel = "di",
  withShadow = true,
  gradientColor = "#3d6289",
  swiperHeight = 574,
  type = "full-image",
  tagsLabelTitle = "Argomenti correlati",
}) => {
  const LinkTag = asLink || "a";
  const ImageComponent = asImage;
  const swiperRef = useRef<any>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(/* true */ false);
  const toggleAutoPlay = () => {
    setAutoplay(!autoplay);
  };

  const goNext = useCallback(() => {
    if (!swiperRef.current) return;
    const swiper = swiperRef.current;
    if (swiper.isEnd && !swiper.params.loop) {
      swiper.slideTo(0);
    } else {
      swiper.slideNext();
    }
  }, []);

  const goPrev = useCallback(() => {
    if (!swiperRef.current) return;
    const swiper = swiperRef.current;
    if (swiper.isBeginning && !swiper.params.loop) {
      swiper.slideTo(swiper.slides.length - 1);
    } else {
      swiper.slidePrev();
    }
  }, []);

  useEffect(() => {
    const intervalId = autoplay
      ? setInterval(() => {
          goNext();
        }, timeout)
      : null;

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoplay, goNext, timeout]);

  const gradientColorStyle = {
    "--gradient": gradientColor,
  } as CSSProperties;

  const swiperStyle = {
    "--swiper-height": `${swiperHeight}px`,
  } as CSSProperties;

  return (
    <Col
      className={clsx("ui-slider-section-wrapper-container", type)}
      style={swiperStyle}
      aria-label="Slider"
      as="section"
    >
      <div className="ui-slider">
        <Swiper
          modules={[Navigation, Pagination, A11y]}
          slidesPerView={1}
          autoplay={autoplay}
          observer={true}
          observeParents={true}
          loop={true}
          watchSlidesProgress
          onSwiper={(swiper: any) => (swiperRef.current = swiper)}
          onSlideChange={(swiper: any) => {
            setCurrentIndex(swiper.realIndex);
            swiper.slides.forEach((slide: any) => {
              slide.inert = !slide.classList.contains("swiper-slide-visible");
            });
          }}
          onInit={(swiper: any) => {
            swiper.slides.forEach((slide: any) => {
              slide.inert = !slide.classList.contains("swiper-slide-visible");
            });
          }}
          style={swiperStyle}
        >
          {slides.map((slide, index) => (
            <SwiperSlide
              key={index}
              className={clsx(
                type === "blur-image"
                  ? "ui-slider-section-wrapper-container"
                  : "",
              )}
            >
              {type === "blur-image" ? (
                <BlurImageSlide
                  {...slide}
                  ImageComponent={ImageComponent}
                  tagsLabelTitle={tagsLabelTitle}
                  withShadow={withShadow}
                  gradientStyle={gradientColorStyle}
                />
              ) : (
                <>
                  <ImageComponent
                    src={slide.src}
                    alt={slide.alt}
                    className="slider-image"
                    onLoad={() => swiperRef.current?.update?.()}
                  />
                  <div
                    className={clsx("slider-content", {
                      "with-shadow": withShadow,
                    })}
                    style={gradientColorStyle}
                  >
                    <div className="container">
                      <div className="slider-content-grid">
                        <div className="category">
                          {slide.category && (
                            <File fillColor="#fff" width={30} height={30} />
                          )}
                          {slide.category?.type ?? " "}
                          <p className="date">
                            {slide.category?.published_at ?? " "}
                          </p>
                        </div>
                        <LinkTag
                          href={slides[currentIndex]?.href || ""}
                          target={openInNewTab ? "_blank" : "_self"}
                        >
                          <h1 className="title">{slide.title}</h1>
                        </LinkTag>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </SwiperSlide>
          ))}
          <SliderPagination
            slides={slides}
            currentIndex={currentIndex}
            goPrev={goPrev}
            goNext={goNext}
            autoplay={autoplay}
            toggleAutoPlay={toggleAutoPlay}
            type={type}
            gradientColorStyle={gradientColorStyle}
            paginationLabel={paginationLabel}
          />
        </Swiper>
      </div>
    </Col>
  );
};
